import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, ChevronRight, Clock, CheckCircle, Truck, XCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const OrdersPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [isOrdersLoading, setIsOrdersLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/giris', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!isAuthenticated) return;
      
      try {
        const response = await fetch(`${backendUrl}/api/orders`, {
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          setOrders(data);
        }
      } catch (error) {
        console.error('Orders fetch error:', error);
      } finally {
        setIsOrdersLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated, backendUrl]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="text-yellow-500" size={20} />;
      case 'confirmed':
        return <CheckCircle className="text-blue-500" size={20} />;
      case 'processing':
        return <AlertCircle className="text-orange-500" size={20} />;
      case 'shipped':
        return <Truck className="text-purple-500" size={20} />;
      case 'delivered':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'cancelled':
        return <XCircle className="text-red-500" size={20} />;
      default:
        return <Clock className="text-gray-500" size={20} />;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: 'Beklemede',
      confirmed: 'Onaylandı',
      processing: 'Hazırlanıyor',
      shipped: 'Kargoya Verildi',
      delivered: 'Teslim Edildi',
      cancelled: 'İptal Edildi',
    };
    return statusMap[status] || status;
  };

  const getPaymentStatusText = (status) => {
    const statusMap = {
      pending: 'Ödeme Bekleniyor',
      paid: 'Ödendi',
      failed: 'Ödeme Başarısız',
      refunded: 'İade Edildi',
    };
    return statusMap[status] || status;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Siparişi iptal etmek istediğinize emin misiniz?')) return;

    try {
      const response = await fetch(`${backendUrl}/api/orders/${orderId}/cancel`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        setOrders(orders.map(order => 
          order.order_id === orderId 
            ? { ...order, status: 'cancelled' }
            : order
        ));
        setSelectedOrder(null);
      }
    } catch (error) {
      console.error('Cancel order error:', error);
    }
  };

  if (isLoading || isOrdersLoading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-light tracking-wider mb-2">SİPARİŞLERİM</h1>
          <p className="text-gray-500">Tüm siparişlerinizi buradan takip edebilirsiniz</p>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white p-12 text-center">
            <Package size={64} className="mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-light mb-2">Henüz siparişiniz yok</h2>
            <p className="text-gray-500 mb-6">İlk siparişinizi vermek için ürünlerimizi keşfedin</p>
            <Link
              to="/urunler"
              className="inline-block bg-black text-white px-8 py-3 text-sm tracking-wider hover:bg-gray-900 transition-colors"
            >
              ALIŞVERİŞE BAŞLA
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.order_id} className="bg-white shadow-sm">
                {/* Order Header */}
                <div 
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setSelectedOrder(selectedOrder === order.order_id ? null : order.order_id)}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(order.status)}
                      <div>
                        <p className="font-medium">{order.order_id}</p>
                        <p className="text-sm text-gray-500">{formatDate(order.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-medium">{formatPrice(order.total)}</p>
                        <p className={`text-sm ${order.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                          {getPaymentStatusText(order.payment_status)}
                        </p>
                      </div>
                      <ChevronRight 
                        size={20} 
                        className={`text-gray-400 transition-transform ${selectedOrder === order.order_id ? 'rotate-90' : ''}`} 
                      />
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="flex items-center gap-2">
                    <span className={`text-sm px-3 py-1 rounded-full ${
                      order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                      order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                      order.status === 'shipped' ? 'bg-purple-100 text-purple-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {getStatusText(order.status)}
                    </span>
                    <span className="text-sm text-gray-500">
                      {order.items?.length} ürün
                    </span>
                    {/* Tracking Number Badge */}
                    {order.tracking_number && (
                      <span className="text-sm px-3 py-1 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
                        <Truck size={14} />
                        Kargo: {order.tracking_number}
                      </span>
                    )}
                  </div>
                </div>

                {/* Order Details (Expanded) */}
                {selectedOrder === order.order_id && (
                  <div className="border-t px-6 py-4">
                    {/* Items */}
                    <div className="space-y-4 mb-6">
                      {order.items?.map((item, index) => (
                        <div key={index} className="flex gap-4">
                          {item.image && (
                            <img
                              src={item.image}
                              alt={item.product_name}
                              className="w-20 h-20 object-cover"
                            />
                          )}
                          <div className="flex-1">
                            <p className="font-medium">{item.product_name}</p>
                            <p className="text-sm text-gray-500">
                              Boyut: {item.size} | Renk: {item.color}
                            </p>
                            <p className="text-sm">
                              {item.quantity} x {formatPrice(item.price)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">{formatPrice(item.price * item.quantity)}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Shipping Info */}
                    <div className="border-t pt-4 mb-4">
                      <h4 className="font-medium mb-2">Teslimat Adresi</h4>
                      <p className="text-sm text-gray-600">
                        {order.shipping_name}<br />
                        {order.shipping_address}<br />
                        {order.shipping_district}, {order.shipping_city} {order.shipping_postal_code}<br />
                        Tel: {order.shipping_phone}
                      </p>
                    </div>

                    {/* Tracking Info */}
                    {order.tracking_number && (
                      <div className="border-t pt-4 mb-4">
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <Truck size={18} className="text-purple-600" />
                          Kargo Takip
                        </h4>
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600 mb-2">Takip Numarası:</p>
                          <p className="font-mono text-lg font-medium text-purple-700">
                            {order.tracking_number}
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            Kargo firmanızın web sitesinden bu numara ile siparişinizi takip edebilirsiniz.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Order Summary */}
                    <div className="border-t pt-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span>Ara Toplam</span>
                        <span>{formatPrice(order.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-sm mb-2">
                        <span>Kargo</span>
                        <span>{order.shipping_cost === 0 ? 'Ücretsiz' : formatPrice(order.shipping_cost)}</span>
                      </div>
                      <div className="flex justify-between font-medium text-lg border-t pt-2">
                        <span>Toplam</span>
                        <span>{formatPrice(order.total)}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    {order.status === 'pending' && order.payment_status === 'pending' && (
                      <div className="border-t pt-4 mt-4 flex gap-4">
                        {order.shopier_payment_url && (
                          <a
                            href={order.shopier_payment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 bg-black text-white py-3 text-center text-sm tracking-wider hover:bg-gray-900 transition-colors"
                          >
                            ÖDEME YAP
                          </a>
                        )}
                        <button
                          onClick={() => handleCancelOrder(order.order_id)}
                          className="px-6 py-3 border border-red-500 text-red-500 text-sm tracking-wider hover:bg-red-50 transition-colors"
                        >
                          İPTAL ET
                        </button>
                      </div>
                    )}

                    {(order.status === 'confirmed' || order.status === 'processing') && (
                      <div className="border-t pt-4 mt-4">
                        <button
                          onClick={() => handleCancelOrder(order.order_id)}
                          className="w-full py-3 border border-red-500 text-red-500 text-sm tracking-wider hover:bg-red-50 transition-colors"
                        >
                          SİPARİŞİ İPTAL ET
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
